import { Link } from "react-router-dom";
import EnlaceCotizar from "../components/ui/EnlaceCotizar.jsx";
import ProductoCard from "../components/catalogo/ProductoCard.jsx";
import Foto from "../components/ui/Foto.jsx";
import { useProductos, useCategorias, useServicios } from "../lib/useCatalogo";
import { EMPRESA, MAQUINAS, esPendiente } from "../lib/config";
import s from "./Home.module.css";

export default function Home() {
  const { productos } = useProductos();
  const { categorias } = useCategorias();
  const { servicios } = useServicios();

  const destacados = productos.filter((p) => p.destacado).slice(0, 4);

  return (
    <>
      <Hero />
      <Procesos servicios={servicios} />
      <Taller />
      <Material categorias={categorias} destacados={destacados} />
      <ComoTrabajamos />
      <CtaFinal />
    </>
  );
}

/* ------------------------------------------------------------------ */

function Hero() {
  return (
    <section className={s.hero}>
      {/* Foto de la plegadora a sangre. El velo de encima garantiza el
          contraste del titular sobre cualquier zona de la imagen. */}
      <div className={s.heroFoto}>
        <Foto
          nombre="plegadora"
          alt=""
          className={s.heroImg}
          sizes="100vw"
          prioridad
        />
        <div className={s.heroVelo} aria-hidden="true" />
      </div>

      <div className={s.heroFondo} aria-hidden="true">
        <svg viewBox="0 0 1440 600" preserveAspectRatio="xMidYMid slice">
          <defs>
            <pattern
              id="heroRejilla"
              width="48"
              height="48"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M48 0H0v48"
                fill="none"
                stroke="#ffffff"
                strokeWidth="1"
                opacity="0.05"
              />
            </pattern>
          </defs>
          <rect width="1440" height="600" fill="url(#heroRejilla)" />
          {/* Plancha entrando a la plegadora: el gesto del logotipo */}
          <g opacity="0.1" stroke="#ffffff" strokeWidth="2" fill="none">
            <path d="M940 140h300M940 170h300" />
            <path d="M1000 250l90 70 90-70" strokeWidth="3" />
            <path d="M960 400h130l90 70 90-70h130" strokeWidth="3" />
            <circle cx="1180" cy="320" r="4" fill="#ffffff" stroke="none" />
          </g>
        </svg>
      </div>

      <div className={`container ${s.heroInner}`}>
        <p className={s.heroEyebrow}>
          <span className={s.puntoVivo} /> Planta en {EMPRESA.ciudad}
        </p>

        <h1 className={s.heroTitulo}>
          Cortamos, plegamos
          <br />
          <span className={s.heroAcento}>y rolamos acero</span>
        </h1>

        <p className={s.heroTexto}>
          Corte laser, oxicorte, plegado y rolado de plancha con maquinaria
          propia. Ademas vendemos el material: planchas metalicas y ejes
          redondos solidos.
        </p>

        <div className={s.heroAcciones}>
          <EnlaceCotizar className="btn btn--accent">
            Cotizar mi pieza
          </EnlaceCotizar>
          <Link to="/servicios" className="btn btn--onDark">
            Ver los procesos
          </Link>
        </div>

        <dl className={s.heroCifras}>
          <Cifra valor="Corte" etiqueta="Laser y oxicorte" />
          <Cifra valor="Plegado" etiqueta="Doblez segun plano" />
          <Cifra valor="Rolado" etiqueta="Cilindros y virolas" />
        </dl>
      </div>
    </section>
  );
}

function Cifra({ valor, etiqueta }) {
  return (
    <div className={s.cifra}>
      <dt className={s.cifraValor}>{valor}</dt>
      <dd className={s.cifraEtiqueta}>{etiqueta}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Procesos({ servicios }) {
  return (
    <section className="section container">
      <p className="eyebrow">01 / Lo que hacemos</p>
      <div className={s.encabezado}>
        <h2>Cuatro procesos sobre la misma plancha</h2>
        <p className="lead">
          Traes el plano o la muestra y nosotros entregamos la pieza lista para
          soldar, montar o pintar.
        </p>
      </div>

      <div className={s.gridServicios}>
        {servicios.map((sv, i) => (
          <article key={sv.id} className={s.servicio}>
            <span className={`mono ${s.servicioNum}`}>
              {String(i + 1).padStart(2, "0")}
            </span>
            <h3 className={s.servicioNombre}>{sv.nombre}</h3>
            <p className={s.servicioResumen}>{sv.resumen}</p>
          </article>
        ))}
      </div>

      <Link to="/servicios" className={`btn btn--ghost ${s.servicioCta}`}>
        Conocer cada proceso
      </Link>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function Taller() {
  return (
    <section className="section section--surface">
      <div className="container">
        <p className="eyebrow">02 / El taller</p>
        <div className={s.encabezado}>
          <h2>Maquinaria propia, no tercerizada</h2>
          <p className="lead">
            El trabajo se hace en nuestra planta, asi que controlamos el plazo y
            la calidad de principio a fin.
          </p>
        </div>

        <figure className={s.planta}>
          <Foto
            nombre="taller"
            alt="Planta de Grupo Aceros Lima: plegadoras, guillotina y mesas de trabajo"
            className={s.plantaFoto}
            sizes="(max-width: 1280px) 100vw, 1280px"
          />
          <figcaption className={s.plantaPie}>
            Nuestra planta en {EMPRESA.ciudad}: plegadoras, guillotina y mesas
            de habilitado bajo un mismo techo.
          </figcaption>
        </figure>

        <div className={s.gridMaquinas}>
          {MAQUINAS.map((m) => (
            <figure key={m.id} className={s.maquina}>
              <Foto
                nombre={m.id}
                alt={m.nombre}
                className={s.maquinaFoto}
                sizes="(max-width: 700px) 100vw, (max-width: 1100px) 50vw, 33vw"
              />
              <figcaption className={s.maquinaPie}>
                <h3 className={s.maquinaNombre}>{m.nombre}</h3>
                <p className={s.maquinaTexto}>{m.descripcion}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function Material({ categorias, destacados }) {
  return (
    <section className="section container">
      <p className="eyebrow">03 / Material</p>
      <div className={s.encabezado}>
        <h2>Tambien vendemos el acero</h2>
        <Link to="/productos" className="btn btn--ghost btn--sm">
          Ver catalogo completo
        </Link>
      </div>

      {categorias.length === 0 && (
        <p className={s.categoriaResumen}>
          Estamos cargando el catalogo de materiales. Escribenos con tu lista y
          te cotizamos mientras tanto.
        </p>
      )}

      <div className={s.gridCategorias}>
        {categorias.map((c) => (
          <Link
            key={c.id}
            to={`/productos?categoria=${c.slug || c.id}`}
            className={s.categoria}
          >
            <span className={`mono ${s.categoriaIndice}`}>
              {String(c.orden ?? 0).padStart(2, "0")}
            </span>
            <h3 className={s.categoriaNombre}>{c.nombre}</h3>
            <p className={s.categoriaResumen}>{c.resumen}</p>
            <span className={s.categoriaFlecha} aria-hidden="true">
              &rarr;
            </span>
          </Link>
        ))}
      </div>

      {destacados.length > 0 && (
        <div className={s.gridProductos}>
          {destacados.map((p) => (
            <ProductoCard key={p.id} producto={p} />
          ))}
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */

function ComoTrabajamos() {
  const pasos = [
    {
      titulo: "Nos envias el plano",
      texto:
        "Un DXF, un PDF, una foto de la pieza o un croquis a mano. Con eso basta para empezar.",
    },
    {
      titulo: "Revisamos y cotizamos",
      texto:
        "Confirmamos material, espesor y proceso, y te devolvemos el precio con IGV y plazo de entrega.",
    },
    {
      titulo: "Fabricamos",
      texto:
        "Cortamos, plegamos o rolamos en nuestra planta, con el material tuyo o vendido por nosotros.",
    },
    {
      titulo: "Entregamos",
      texto:
        "Recoges en planta o lo despachamos. La pieza sale rotulada y lista para el siguiente proceso.",
    },
  ];

  return (
    <section className="section section--dark">
      <div className="container">
        <p className="eyebrow">04 / Como trabajamos</p>
        <h2 className={s.tituloOscuro}>Del plano a la pieza</h2>

        <div className={s.gridPuntos}>
          {pasos.map((p, i) => (
            <article key={p.titulo} className={s.punto}>
              <span className={`mono ${s.puntoNum}`}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className={s.puntoTitulo}>{p.titulo}</h3>
              <p className={s.puntoTexto}>{p.texto}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function CtaFinal() {
  return (
    <section className={`section--tight ${s.cta}`}>
      <div className={`container ${s.ctaInner}`}>
        <div>
          <h2 className={s.ctaTitulo}>Tienes un plano listo?</h2>
          <p className={s.ctaTexto}>
            Envianos la pieza que necesitas y te respondemos con precio, proceso
            y plazo.
          </p>
        </div>
        <div className={s.ctaAcciones}>
          <EnlaceCotizar className="btn btn--accent">
            Cotizar ahora
          </EnlaceCotizar>
          {!esPendiente(EMPRESA.telefono) && (
            <a href={`tel:${EMPRESA.telefono}`} className="btn btn--ghost">
              Llamar a ventas
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
